import { type Component, Show } from 'solid-js'
import Button from '~/components/Button'
import Input from '~/components/Input'
import MaskInput from '~/components/MaskInput'
import type { DiscountTypeType } from './line'

export interface DiscountFieldPropsType {
  label?: string
  value: number
  type: DiscountTypeType
  currency: string
  /** Locale de Intl para el MaskInput de moneda. */
  locale?: string
  /** Monto ya resuelto, para avisar cuánto representa el porcentaje. */
  resolved?: number
  formatMoney: (value: number) => string
  onValueChange: (value: number) => void
  onTypeChange: (type: DiscountTypeType) => void
}

/**
 * Descuento en dinero o en porcentaje. El selector va pegado al campo porque
 * cambiar de uno a otro reinterpreta el número que hay escrito: verlos juntos
 * evita cobrar un 20% creyendo que eran 20 pesos.
 */
const DiscountField: Component<DiscountFieldPropsType> = props => {
  const label = () => props.label ?? 'Descuento'

  const switchType = (type: DiscountTypeType) => {
    if (type === props.type) return
    props.onTypeChange(type)
    // El número escrito no significa lo mismo en la otra unidad: se limpia.
    props.onValueChange(0)
  }

  const ToggleButton = () => (
    <Button
      type="button"
      size="sm"
      variant="soft"
      color={props.type === 'amount' ? 'primary' : 'default'}
      class="text-xs font-bold"
      onClick={(e: MouseEvent) => {
        e.stopPropagation()
        switchType(props.type === 'amount' ? 'percent' : 'amount')
      }}
      tooltip={props.type === 'amount' ? 'Cambiar a porcentaje' : 'Cambiar a dinero'}
    >
      {props.type === 'amount' ? '$' : '%'}
    </Button>
  )

  return (
    <div>
      <Show
        when={props.type === 'percent'}
        fallback={
          <MaskInput
            label={label()}
            maskType="currency"
            currency={props.currency}
            locale={props.locale ?? 'es-CO'}
            value={props.value ? String(props.value) : ''}
            onValueChange={v => props.onValueChange(Number(v) || 0)}
            endIcon={<ToggleButton />}
          />
        }
      >
        <Input
          label={`${label()} (%)`}
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={props.value || ''}
          onInput={e => props.onValueChange(Math.min(Math.max(Number(e.currentTarget.value) || 0, 0), 100))}
          endIcon={<ToggleButton />}
        />
      </Show>
      <Show when={props.type === 'percent' && props.value > 0 && (props.resolved ?? 0) > 0}>
        <p class="mt-1 text-xs text-foreground-subtle">Equivale a {props.formatMoney(props.resolved ?? 0)}</p>
      </Show>
    </div>
  )
}

export default DiscountField
